import React, { useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocket/WebSocketService';
import { getGameManager } from '@game/GameManager';
import { useRoomStore } from '../store/RoomStore';

interface AgentInfo { id: string; color: string; }
interface Position { x: number; y: number; }
interface ScenarioInfo { name: string; description?: string }
const TILE_SIZE = 24;
const pxToGrid = (p: Position): Position => ({ x: Math.floor((p.x - TILE_SIZE/2)/TILE_SIZE), y: Math.floor((p.y - TILE_SIZE/2)/TILE_SIZE) });
const DEFAULT_FAKE_SCENARIOS: ScenarioInfo[] = [
  { name: 'move_then_collect', description: 'Move multiple steps then collect resource' },
  { name: 'move_into_combat', description: 'Movement followed by combat' },
  { name: 'combat_then_move', description: 'Combat then winner moves' },
  { name: 'combat_vs_safezone', description: 'Combat while another dies to safezone' },
  { name: 'move_then_use_item', description: 'Movement then use item (if backend supports)' },
  { name: 'long_combat_with_death', description: 'Long combat chain with elimination' }
];

const CompleteTest_FrontBackend: React.FC = () => {
  const getInitialRoomId = () => {
    try {
      if (typeof window !== 'undefined' && (window as any).__ABR_CURRENT_ROOM_ID__) {
        return String((window as any).__ABR_CURRENT_ROOM_ID__);
      }
      const existing = useRoomStore?.getState?.()?.currentRoom?.id;
      if (existing) return existing;
    } catch {}
    return 'room_dev_one_turn';
  };

  const [socketUrl, setSocketUrl] = useState<string>(() => {
    try { return webSocketService.getConfig().url; } catch { return 'ws://localhost:8081'; }
  });
  const [roomIdInput, setRoomIdInput] = useState<string>(getInitialRoomId);
  const [connected, setConnected] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [inventory, setInventory] = useState<Record<string, Array<{ id: string; name: string; type: string }>>>({});
  const [turn, setTurn] = useState<number>(0);
  const [medical, setMedical] = useState<{ id: string; pos: Position }|null>(null);
  const [weapon, setWeapon] = useState<{ id: string; pos: Position }|null>(null);
  const [agents, setAgents] = useState<Record<string, AgentInfo>>({});
  const survivorsRef = useRef<string[]>([]);
  const [scenarioList, setScenarioList] = useState<ScenarioInfo[]>([]);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);
  const [scenarioStatus, setScenarioStatus] = useState<string>('idle');

  const log = (s: string) => setLogs(prev => [`[${new Date().toLocaleTimeString()}] ${s}`, ...prev].slice(0, 400));

  const scenarioChoices = scenarioList.length ? scenarioList : DEFAULT_FAKE_SCENARIOS;
  const scenarioRunningLabel = activeScenario ? `${activeScenario} (${scenarioStatus})` : 'Idle';
  const scenarioButtonsDisabled = !connected || !!activeScenario;

  const bindRoomId = async (explicitId?: string) => {
    const rid = (explicitId ?? roomIdInput ?? '').trim() || 'room_dev_one_turn';
    try {
      useRoomStore.setState((state) => {
        if (!state.currentRoom || state.currentRoom.id !== rid) {
          state.currentRoom = {
            id: rid,
            name: rid,
            participantCount: state.currentRoom?.participantCount ?? 0,
            maxParticipants: state.currentRoom?.maxParticipants ?? 10,
            spectatorCount: state.currentRoom?.spectatorCount ?? 0,
            status: state.currentRoom?.status ?? 'ACTIVE',
            createdBy: state.currentRoom?.createdBy ?? 'dev-tools',
            createdAt: state.currentRoom?.createdAt ?? new Date().toISOString(),
            participants: state.currentRoom?.participants ?? []
          } as any;
        }
        state.isInRoom = true;
        state.isSpectating = false;
        state.gameId = rid;
      });
    } catch (err) {
      console.warn('bindRoomId setState failed', err);
    }
    try { (window as any).__ABR_CURRENT_ROOM_ID__ = rid; } catch {}
    try { getGameManager().setCurrentRoomId(rid); } catch {}
    log(`Room bound: ${rid}`);
    return rid;
  };

  useEffect(() => {
    // Boot Phaser Game (embedded) for map/animation rendering
    let mounted = true;
    (async () => {
      try {
        const gm = getGameManager();
        // Skip WebSocket connect here; we control it via the Connect button
        await (gm as any).initialize?.({ connect: false });
        if (mounted) gm.start();
      } catch (e) { console.error('Phaser boot failed:', e); }
    })();

    // Listen for scene map-ready signal (frontend-side ACK trigger)
    const onMapReady = () => {
      setMapReady(true);
      log('Map ready (frontend).');
    };
    window.addEventListener('ABR_MAP_READY', onMapReady);

    // bridge listeners
    const onTurnStarted = (d:any) => { setTurn(d?.turnNumber||0); log(`TURN_STARTED ${d?.turnNumber}`); };
    const onSZUpdated = (d:any) => { log(`SAFEZONE_UPDATED r=${d?.currentRadius} next=${d?.nextRadius}`); };
    const onSZWarn = (d:any) => { log(`SAFEZONE_WARNING nextTurn=${d?.contraction?.scheduledTurn}`); };
    const onSZStart = (d:any) => { log(`SAFEZONE_CONTRACTION_STARTED to r=${d?.contraction?.to?.radius}`); };
    const onSZDone  = (d:any) => { log(`SAFEZONE_CONTRACTION_COMPLETED r=${d?.safeZone?.radius}`); };
    const onSpawn = (d:any) => {
      const ag = d?.agent || d; if (!ag?.id) return;
      setAgents(prev => ({ ...prev, [ag.id]: { id: ag.id, color: ag.color || 'default' } }));
      log(`AGENT_SPAWNED ${ag.id} @(${ag.position?.x},${ag.position?.y}) color=${ag.color||'default'}`);
    };
    const onMoved = (d:any) => log(`AGENT_MOVED ${d?.agentId}→(${(d?.toPosition||d?.position)?.x},${(d?.toPosition||d?.position)?.y})`);
    const onHP   = (d:any) => log(`AGENT_HEALTH_CHANGED ${d?.agentId} ${d?.previousHealth?.current}→${d?.currentHealth?.current}`);
    const onEnv  = (d:any) => log(`ENVIRONMENT_DAMAGE total=${d?.totalDamage}`);
    const onResSpawn = (p:any) => {
      const items = Array.isArray(p?.newItems) ? p.newItems : [];
      items.forEach((it:any) => {
        const cat = String(it?.category||'').toLowerCase();
        const pos = it?.position; if (!pos) return;
        if (!medical && (cat==='medical'||cat==='consumable')) setMedical({ id: String(it.itemId||it.resourceId), pos });
        if (!weapon && cat==='weapon') setWeapon({ id: String(it.itemId||it.resourceId), pos });
      });
      if (items.length) log(`RESOURCE_SPAWNED count=${items.length}`);
    };
    // 简易背包面板：根据事件维护每个agent的物品列表（仅用于演示/诊断）
    const upsertItem = (agentId:string, item:any) => {
      setInventory(prev => {
        const list = prev[agentId] ? [...prev[agentId]] : [];
        const id = String(item?.itemId || item?.id || '');
        const name = String(item?.name || item?.type || item?.weaponType || 'ITEM');
        const type = String(item?.category || item?.type || item?.weaponType || 'unknown').toUpperCase();
        if (id) {
          const idx = list.findIndex(x => x.id === id);
          if (idx >= 0) list[idx] = { id, name, type };
          else list.push({ id, name, type });
        }
        return { ...prev, [agentId]: list };
      });
    };
    const removeItem = (agentId:string, itemId:string) => {
      setInventory(prev => {
        const list = prev[agentId] ? [...prev[agentId]] : [];
        const filtered = list.filter(x => x.id !== itemId);
        return { ...prev, [agentId]: filtered };
      });
    };
    const onResourceCollected = (d:any) => {
      const aid = d?.agentId;
      const item = d?.item || { itemId: d?.itemId, name: d?.name, type: d?.type, category: d?.category };
      if (aid && item) upsertItem(aid, item);
      log(`RESOURCE_COLLECTED by ${aid}`);
    };
    const onWeaponEquipped = (d:any) => {
      const aid = d?.agentId;
      const item = d?.weapon || { itemId: d?.itemId, name: d?.name, type: d?.weaponType || 'WEAPON', category: 'weapon' };
      if (aid && item) upsertItem(aid, item);
      log(`WEAPON_EQUIPPED by ${aid}`);
    };
    const onItemUsed = (d:any) => {
      const aid = d?.agentId;
      const itemId = String(d?.itemId || '');
      if (aid && itemId) removeItem(aid, itemId);
      log(`ITEM_USED by ${aid}`);
    };
    webSocketService.onGameEvent('RESOURCE_COLLECTED', onResourceCollected);
    webSocketService.onGameEvent('WEAPON_EQUIPPED', onWeaponEquipped);
    webSocketService.onGameEvent('ITEM_USED', onItemUsed);
    webSocketService.onGameEvent('TURN_STARTED', onTurnStarted);
    webSocketService.onGameEvent('SAFEZONE_UPDATED', onSZUpdated);
    webSocketService.onGameEvent('SAFEZONE_WARNING', onSZWarn);
    webSocketService.onGameEvent('SAFEZONE_CONTRACTION_STARTED', onSZStart);
    webSocketService.onGameEvent('SAFEZONE_CONTRACTION_COMPLETED', onSZDone);
    webSocketService.onGameEvent('AGENT_SPAWNED', onSpawn);
    webSocketService.onGameEvent('AGENT_MOVED', onMoved);
    webSocketService.onGameEvent('AGENT_HEALTH_CHANGED', onHP);
    webSocketService.onGameEvent('ENVIRONMENT_DAMAGE', onEnv);
    webSocketService.onGameEvent('RESOURCE_SPAWNED', onResSpawn);
    return () => {
      mounted = false;
      try { const gm = getGameManager(); gm.destroy(); } catch {}
      try { window.removeEventListener('ABR_MAP_READY', onMapReady); } catch {}
      webSocketService.off('TURN_STARTED', onTurnStarted);
      webSocketService.off('SAFEZONE_UPDATED', onSZUpdated);
      webSocketService.off('SAFEZONE_WARNING', onSZWarn);
      webSocketService.off('SAFEZONE_CONTRACTION_STARTED', onSZStart);
      webSocketService.off('SAFEZONE_CONTRACTION_COMPLETED', onSZDone);
      webSocketService.off('AGENT_SPAWNED', onSpawn);
      webSocketService.off('AGENT_MOVED', onMoved);
      webSocketService.off('AGENT_HEALTH_CHANGED', onHP);
      webSocketService.off('ENVIRONMENT_DAMAGE', onEnv);
      webSocketService.off('RESOURCE_SPAWNED', onResSpawn);
      webSocketService.off('RESOURCE_COLLECTED', onResourceCollected);
      webSocketService.off('WEAPON_EQUIPPED', onWeaponEquipped);
      webSocketService.off('ITEM_USED', onItemUsed);
    };
  }, []);

  useEffect(() => {
    const onStarted = (payload: any) => {
      const name = payload?.name || payload?.scenario || 'unknown';
      setActiveScenario(name);
      setScenarioStatus('running');
      log(`[Scenario] started: ${name}`);
    };
    const onCompleted = (payload: any) => {
      const name = payload?.name || 'unknown';
      setScenarioStatus('completed');
      setActiveScenario(null);
      log(`[Scenario] completed: ${name}`);
    };
    const onAborted = (payload: any) => {
      const name = payload?.name || 'unknown';
      const error = payload?.error;
      setScenarioStatus('aborted');
      setActiveScenario(null);
      log(`[Scenario] aborted: ${name}${error ? ` (${error})` : ''}`);
    };
    try {
      webSocketService.onServerEvent('scenario:started', onStarted);
      webSocketService.onServerEvent('scenario:completed', onCompleted);
      webSocketService.onServerEvent('scenario:aborted', onAborted);
    } catch {}
    return () => {
      try {
        webSocketService.offServerEvent('scenario:started', onStarted);
        webSocketService.offServerEvent('scenario:completed', onCompleted);
        webSocketService.offServerEvent('scenario:aborted', onAborted);
      } catch {}
    };
  }, []);

  // Keep UI "connected" state in sync even if implicit connect happens via emit()
  useEffect(() => {
    const id = setInterval(() => {
      try { setConnected(webSocketService.isConnected()); } catch {}
    }, 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!connected) return;
    refreshScenarioList();
  }, [connected]);

  const connect = async () => {
    const rid = await bindRoomId();
    try {
      await webSocketService.connect(socketUrl);
      setConnected(true);
      log(`Connected ${socketUrl} (room=${rid})`);
    }
    catch(e:any){ setConnected(false); log(`Connect failed: ${e?.message||e}`); }
  };

  const refreshScenarioList = async () => {
    if (!webSocketService.isConnected()) {
      log('Scenario list unavailable: socket not connected');
      return;
    }
    setScenarioLoading(true);
    try {
      const ack: any = await webSocketService.emit('dev:scenario:list');
      if (Array.isArray(ack?.scenarios)) {
        setScenarioList(ack.scenarios);
        log(`Scenario list refreshed (${ack.scenarios.length})`);
      } else {
        log('Scenario list request returned empty result');
      }
    } catch (e:any) {
      log(`Scenario list error: ${e?.message||e}`);
    } finally {
      setScenarioLoading(false);
    }
  };

  const runScenario = async (name: string) => {
    if (!name) return;
    await bindRoomId();
    try {
      const ack: any = await webSocketService.emit('dev:scenario:run', { name });
      if (ack?.success) {
        setScenarioStatus('queued');
        log(`[Scenario] run request accepted: ${name}`);
      } else {
        log(`[Scenario] run request failed (${name}): ${ack?.error || 'unknown'}`);
      }
    } catch (e:any) {
      log(`[Scenario] run error (${name}): ${e?.message||e}`);
    }
  };

  const stopScenario = async () => {
    try {
      const ack: any = await webSocketService.emit('dev:scenario:stop');
      if (ack?.success) {
        log('[Scenario] stop requested');
      } else {
        log(`[Scenario] stop failed: ${ack?.error || 'no scenario running'}`);
      }
    } catch (e:any) {
      log(`[Scenario] stop error: ${e?.message||e}`);
    }
  };

  // Helpers to emit control commands
  const startGame = async () => {
    // 为避免过快推进，先将回合间隔拉长（30s/turn）
    await webSocketService.emit('game:setSpeed', { intervalMs: 30000 });
    const r: any = await webSocketService.emit('game:start');
    if (r && (r as any).success === false) {
      log(`Start refused by backend: ${(r as any).error}`);
      return;
    }
    log('Game started with 30000ms/turn');
  };
  // 一键启动“完整集成赛局”：后端自动注册10名默认Agent并开始循环
  const startFullIntegrationGame = async () => {
    try {
      const ack: any = await webSocketService.emit('dev:engine:startFullGame', {});
      if (ack?.success) {
        log(`Full game started. registered=${ack.registered}`);
      } else {
        log(`Full game start failed: ${ack?.error || 'unknown'}`);
      }
    } catch(e:any) {
      log(`Full game start error: ${e?.message||e}`);
    }
  };
  const stopGame  = async () => { await webSocketService.emit('game:stop'); log('Game stopped'); };
  const atdAction = async (agentId: string, type: string, params?: any) => {
    const payload = { agentId, type, params };
    const ack:any = await webSocketService.emit('atd:action', payload);
    log(`atd:action ${type} for ${agentId} → ${ack?.success?'ok':'fail'}`);
  };
  const useMockLLM = async () => { try { const ack:any = await webSocketService.emit('sds:mock', { enabled: true }); log(`sds:mock → ${ack?.success?'mock enabled':'failed'}`);} catch(e:any){ log(`sds:mock failed: ${e?.message||e}`);} };

  // Register 3 agents (white/purple/orange) with sum=150 attributes
  const registerAgents = async () => {
    await bindRoomId();
    const regs = [
      { id:'test_agent1', color:'white',  pos:{ x: 5, y: 5 } },
      { id:'test_agent2', color:'purple', pos:{ x: 6, y: 5 } },
      { id:'test_agent3', color:'orange', pos:{ x: 6, y: 6 } },
    ];
    for (const r of regs) {
      const payload:any = {
        agentId: r.id,
        position: r.pos,
        agentType: 'agent',
        color: r.color,
        // Ensure total points <= 100 per CURRENT_GAMEPLAY_CONFIG
        attributes: { health: 25, strength: 25, defense: 25, speed: 25 } // total=100
      };
      const ack:any = await webSocketService.emit('dev:agent:register', payload);
      log(`register ${r.id} → ${ack?.success?'ok':'fail'}`);
    }
    // 按你的要求：仅为 1/2 真实装备武器（3 保持未装备以便做对照）
    await equipWeaponsForAgents([
      { id:'test_agent1', pos:{ x:5, y:5 }, weapon:'SWORD' },
      { id:'test_agent2', pos:{ x:6, y:5 }, weapon:'SWORD' }
    ]);
  };

  // 一键获取后端地图并注入到前端，立即渲染
  const getMapSnapshot = async () => {
    try {
      await bindRoomId();
      const snap:any = await webSocketService.emit('dev:map:snapshot');
      if (snap?.grid) {
        webSocketService.triggerGameEvent('MAP_GENERATED', { grid: snap.grid, __viz_internal: false });
        setMapReady(true);
        log(`MAP_GENERATED injected: ${snap.grid.length}x${snap.grid[0]?.length||0}`);
      } else {
        log('map snapshot failed: no grid returned');
      }
    } catch(e:any) {
      log(`map snapshot error: ${e?.message||e}`);
    }
  };

  // 生成演示用资源：1把武器 + 1个医疗（靠近中心）
  const spawnDemoResources = async () => {
    try {
      await bindRoomId();
      const items = [
        { type:'WEAPON', position:{ x: 6, y: 5 }, metadata:{ weaponType:'SWORD' } },
        { type:'MEDICAL', position:{ x: 5, y: 6 }, metadata:{ medicalType:'large' } }
      ];
      const ack:any = await webSocketService.emit('dev:resource:spawn', { items });
      log(`resource:spawn → ${ack?.success?'ok':'fail'}`);
    } catch(e:any) {
      log(`resource:spawn error: ${e?.message||e}`);
    }
  };

  // 分散已注册的三个Agent，避免同格重叠
  const spreadAgents = async () => {
    try {
      await bindRoomId();
      const m1:any = await webSocketService.emit('dev:map:moveAgent', { agentId:'test_agent2', toPosition:{ x: 6, y: 5 } });
      const m2:any = await webSocketService.emit('dev:map:moveAgent', { agentId:'test_agent3', toPosition:{ x: 6, y: 6 } });
      log(`spread agents → a2:${m1?.success?'ok':'fail'} a3:${m2?.success?'ok':'fail'}`);
    } catch(e:any) {
      log(`spread agents error: ${e?.message||e}`);
    }
  };

  // ===== 工具：为指定Agent生成并“真实收集(ATD)→自动装备”武器（不再使用前端伪装事件） =====
  const equipWeaponsForAgents = async (list: Array<{ id:string; pos:{x:number;y:number}; weapon:'SWORD'|'AXE'|'LANCE' }>) => {
    try {
      // 1) 在各自坐标生成指定武器（使用统一WEAPON类型+metadata.weaponType）
      const spawnReq = list.map(it => ({ type: 'WEAPON', position: it.pos, metadata: { weaponType: it.weapon } }));
      const spawned:any = await webSocketService.emit('dev:resource:spawn', { items: spawnReq });
      if (!spawned?.success) { log(`equip: spawn failed`); return; }
      const items: Array<{ itemId:string; position:{x:number;y:number} }> = Array.isArray(spawned.items) ? spawned.items : [];
      const findByPos = (p:Position) => items.find(it => it.position?.x===p.x && it.position?.y===p.y)?.itemId;

      // 2) 逐个Agent：MOVE到武器 → ATD:COLLECT(resourceId) → 后端自动equip
      for (const a of list) {
        const rid = findByPos(a.pos);
        if (!rid) { log(`equip: cannot resolve resourceId for ${a.id} at (${a.pos.x},${a.pos.y})`); continue; }
        await atdAction(a.id,'MOVE',{ target: a.pos });
        try { await waitMoveAnim(a.id); } catch { log(`move anim ack timeout (${a.id})`); }
        await atdAction(a.id,'COLLECT',{ resourceId: rid });
        log(`equip(real): ${a.id} COLLECT ${rid}`);
      }
    } catch(e:any) {
      log(`equip batch error: ${e?.message||e}`);
    }
  };

  // Round scripts
  // 等待前端移动动画完成（基于 BattleScene 派发的浏览器事件）
  const waitMoveAnim = (agentId: string, timeout = 4000) => new Promise<void>((resolve, reject) => {
    let tid: any;
    const handler = (e: any) => {
      if (e?.detail?.agentId === agentId) {
        try { window.removeEventListener('AGENT_MOVE_ANIM_DONE' as any, handler as any); } catch {}
        clearTimeout(tid);
        resolve();
      }
    };
    try { window.addEventListener('AGENT_MOVE_ANIM_DONE' as any, handler as any, { once: true }); } catch {}
    tid = setTimeout(() => {
      try { window.removeEventListener('AGENT_MOVE_ANIM_DONE' as any, handler as any); } catch {}
      reject(new Error('move animation timeout'));
    }, timeout);
  });

  const round1 = async () => {
    await bindRoomId();
    // 目标：test_agent1 与 test_agent2 都在本回合“真实收集并装备武器”（走 ATD→COLLECT 原子路径）
    if (!mapReady) { log('map not ready, click Get Map Snapshot first'); return; }
    const w1Pos = { x: 5, y: 5 }; // near agent1
    const w2Pos = { x: 6, y: 5 }; // near agent2
    try {
      // 在两处生成武器（SWORD）
      const spawn:any = await webSocketService.emit('dev:resource:spawn', {
        items: [
          { type: 'WEAPON', position: w1Pos, metadata: { weaponType: 'SWORD' } },
          { type: 'WEAPON', position: w2Pos, metadata: { weaponType: 'SWORD' } }
        ]
      });
      if (!spawn?.success) { log('round1: spawn weapons failed'); return; }

      // 按位置解析 itemId，避免顺序歧义
      const items: Array<{ itemId:string; position:{x:number;y:number}; type:string }>
        = Array.isArray(spawn.items) ? spawn.items : [];
      const findByPos = (p:Position) => items.find(it => it.position?.x===p.x && it.position?.y===p.y)?.itemId;
      const wid1 = findByPos(w1Pos);
      const wid2 = findByPos(w2Pos);
      if (!wid1 || !wid2) { log(`round1: cannot resolve weapon ids (${wid1}, ${wid2})`); return; }

      // agent1 → 武器1 → COLLECT（自动装备）
      await atdAction('test_agent1','MOVE',{ target: w1Pos });
      try { await waitMoveAnim('test_agent1'); } catch { log('move anim ack timeout (agent1)'); }
      await atdAction('test_agent1','COLLECT',{ resourceId: wid1 });

      // agent2 → 武器2 → COLLECT（自动装备）
      await atdAction('test_agent2','MOVE',{ target: w2Pos });
      try { await waitMoveAnim('test_agent2'); } catch { log('move anim ack timeout (agent2)'); }
      await atdAction('test_agent2','COLLECT',{ resourceId: wid2 });

      // agent3 占位等待
      await atdAction('test_agent3','WAIT',{});
      log('round1: both agent1/agent2 should now be EQUIPPED (server-side)');
    } catch (e:any) {
      log(`round1 error: ${e?.message||e}`);
    }
  };

  const round2 = async () => {
    await bindRoomId();
    // move agent1 adjacent to agent2 (simply move next to weapon pos)
    if (!weapon) { log('weapon pos unknown'); return; }
    const wGrid = pxToGrid(weapon.pos);
    const adj: Position = { x: wGrid.x+1, y: wGrid.y };
    await atdAction('test_agent1','MOVE',{ target: adj });
    try { await waitMoveAnim('test_agent1'); } catch { log('move anim ack timeout (agent1)'); }
    await atdAction('test_agent1','ATTACK',{ target: 'test_agent2' });
  };

  const round3 = async () => {
    await bindRoomId();
    // Move survivor to outside safezone (simplified: move far from center)
    // Choose survivor id: if agent2 eliminated, survivor is 1, else 2
    const survivor = survivorsRef.current.find(id => id==='test_agent1' || id==='test_agent2') || 'test_agent1';
    const far: Position = { x: 0, y: 0 }; // push to corner, likely outside on next contraction
    await atdAction(survivor,'MOVE',{ target: far });
  };

  const round4 = async () => { log('Round4: wait for safezone damage (production)'); };

  const round5 = async () => {
    await bindRoomId();
    // Finish with agent3 death via attack: survivor attacks agent3
    const survivor = survivorsRef.current.find(id => id==='test_agent1' || id==='test_agent2') || 'test_agent1';
    await atdAction(survivor,'ATTACK',{ target: 'test_agent3' });
  };

  return (
    <div style={{ padding: 12, fontFamily: 'sans-serif' }}>
      <h2 style={{ margin:'6px 0 12px' }}>Complete Front-Backend Test (Production Chain)</h2>
      <div style={{ display:'flex', gap:12 }}>
        {/* Left control panel */}
        <div style={{ width: 360, minWidth: 320 }}>
          <div style={{ display:'grid', gap:8, marginBottom:12 }}>
            <input value={socketUrl} onChange={e=>setSocketUrl(e.target.value)} style={{ padding:6, fontFamily:'monospace' }} />
            <div style={{ display:'flex', gap:8 }}>
              <input value={roomIdInput} onChange={e=>setRoomIdInput(e.target.value)} placeholder="room id" style={{ flex:1, padding:6, fontFamily:'monospace' }} />
              <button onClick={()=>bindRoomId(roomIdInput)} style={{ flexBasis:140 }}>Bind Room</button>
            </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={connect} style={{ flex:1 }}>Connect</button>
            <button onClick={useMockLLM} style={{ flex:1 }}>Use Mock LLM</button>
          </div>
          <div style={{ border:'1px solid #444', borderRadius:6, padding:10, display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontWeight:600 }}>Fake Backend Scenarios</div>
              <div style={{ fontSize:12, color:'#0ea5e9' }}>Status: {scenarioRunningLabel}</div>
            </div>
            <div style={{ display:'grid', gap:6, gridTemplateColumns:'repeat(2, minmax(0, 1fr))' }}>
              {scenarioChoices.map((sc) => (
                <button
                  key={sc.name}
                  title={sc.description || sc.name}
                  onClick={() => runScenario(sc.name)}
                  disabled={scenarioButtonsDisabled}
                  style={{ opacity: scenarioButtonsDisabled ? 0.6 : 1 }}
                >
                  {sc.name}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={refreshScenarioList} disabled={scenarioLoading || !connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>
                {scenarioLoading ? 'Refreshing…' : 'Refresh List'}
              </button>
              <button onClick={stopScenario} disabled={!activeScenario} style={{ flex:1, opacity: (!activeScenario)?0.6:1 }}>Stop Scenario</button>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={startGame} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Start (auto loop)</button>
            <button onClick={stopGame} style={{ flex:1 }}>Stop Game</button>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={startFullIntegrationGame} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Start Full Integration (10 agents)</button>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={async()=>{ const r:any=await webSocketService.emit('game:prepare'); log(`Prepare: ${r?.success?'ok':'fail'}`); }} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Prepare (manual)</button>
            <button onClick={async()=>{ const r:any=await webSocketService.emit('game:step'); log(`Step → turn ${r?.turn ?? '?'} ${r?.success?'ok':'fail'}`); }} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Step 1 Turn</button>
          </div>
          </div>
          <div style={{ display:'grid', gap:8, marginBottom:12 }}>
            <button onClick={registerAgents}>Register 3 Agents (100)</button>
            <button onClick={round1}>Round 1: Collect</button>
            <button onClick={round2}>Round 2: Combat</button>
            <button onClick={round3}>Round 3: Move Survivor Outside</button>
            <button onClick={round4}>Round 4: Observe SZ Damage</button>
            <button onClick={round5}>Round 5: Finish</button>
            <hr />
            <button onClick={getMapSnapshot}>Get Map Snapshot → Render</button>
            <button onClick={spawnDemoResources}>Spawn Demo Resources (1 weapon + 1 med)</button>
            <button onClick={spreadAgents}>Spread Registered Agents (avoid overlap)</button>
          </div>
          <div style={{ fontSize:12, marginBottom:8 }}>Current Turn: {turn}</div>
          <div style={{ marginBottom:8 }}>
            <h3>Agents</h3>
            <pre style={{ maxHeight: 160, overflowY:'auto', background:'#111', color:'#0f0', padding:8 }}>{JSON.stringify(agents,null,2)}</pre>
          </div>
          <div style={{ marginBottom:8 }}>
            <h3>Known Resources</h3>
            <div>Medical: {medical ? `${medical.id} @(${medical.pos.x},${medical.pos.y})` : '—'}</div>
            <div>Weapon: {weapon ? `${weapon.id} @(${weapon.pos.x},${weapon.pos.y})` : '—'}</div>
          </div>
          <div style={{ marginBottom:8 }}>
            <h3>Inventory</h3>
            {Object.keys(inventory).length === 0 ? (
              <div>—</div>
            ) : (
              <div style={{ maxHeight: 160, overflowY:'auto', background:'#111', color:'#0f0', padding:8 }}>
                {Object.entries(inventory).map(([aid, items]) => (
                  <div key={aid} style={{ marginBottom:6 }}>
                    <div style={{ color:'#6cf' }}>{aid}</div>
                    {items.length === 0 ? (
                      <div style={{ fontFamily:'monospace', color:'#888' }}>empty</div>
                    ) : (
                      items.map(it => (
                        <div key={it.id} style={{ fontFamily:'monospace' }}>{it.type}:{it.name} ({it.id})</div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right map + logs */}
        <div style={{ flex:1, minWidth: 640 }}>
          <div style={{ border:'1px solid #333', height: 560, position:'relative', marginBottom:12 }}>
            <div id="phaser-game" style={{ position:'absolute', inset:0 }} />
          </div>
          <h3>Logs</h3>
          <div style={{ height: 220, overflowY: 'auto', background:'#222', color:'#ddd', padding:8 }}>
            {logs.map((l,i)=>(<div key={i} style={{ fontFamily:'monospace' }}>{l}</div>))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteTest_FrontBackend;
